/**
 * Single source of truth for the Preventative Maintenance timeline grid.
 *
 * The widget reads these constants to compute how many year columns fit in
 * the viewport (ResizeObserver math). The row components import the same
 * constants to build their grid templates. Editing any value here propagates
 * to all four consumers; nothing else hard-codes the pixel values.
 */

export const LABEL_COLUMN_MIN_PX = 220;

/** Fractional weight of the label column when there's room to grow. */
export const LABEL_COLUMN_FR = 1.6;

export const YEAR_COLUMN_MIN_PX = 160;

/** Width budget for one chevron button in the TimelineHeader grid. */
export const CHEVRON_COLUMN_PX = 40;

/** Horizontal margin applied to data rows so they align inside the header chevrons. */
export const ROW_HORIZONTAL_MARGIN_PX = 36;

/** Default visible-year count before the ResizeObserver computes the real one. */
export const DEFAULT_VISIBLE_YEARS = 5;

/** Upper bound on visible years so very wide viewports don't unnecessarily flatten the cells. */
export const MAX_VISIBLE_YEARS = 8;

/** Pixel chrome (label + chevrons + margins) used to compute how many year columns fit. */
export const NON_YEAR_CHROME_PX = LABEL_COLUMN_MIN_PX + CHEVRON_COLUMN_PX * 2 + ROW_HORIZONTAL_MARGIN_PX * 2;

/** Grid template for the data rows (EquipmentTypeRow, AssetRow). */
export const rowGridTemplate = (yearCount: number): string =>
    `minmax(${LABEL_COLUMN_MIN_PX}px, ${LABEL_COLUMN_FR}fr) repeat(${yearCount}, minmax(${YEAR_COLUMN_MIN_PX}px, 1fr))`;

/** Grid template for the TimelineHeader, which has chevron columns on both sides of the year columns. */
export const headerGridTemplate = (yearCount: number): string =>
    `minmax(${LABEL_COLUMN_MIN_PX}px, ${LABEL_COLUMN_FR}fr) auto repeat(${yearCount}, minmax(${YEAR_COLUMN_MIN_PX}px, 1fr)) auto`;

/** Compute how many year columns fit, given the per-year minimum width. */
export const computeVisibleYears = (containerWidth: number, yearColumnMinPx: number = YEAR_COLUMN_MIN_PX): number => {
    const available = containerWidth - NON_YEAR_CHROME_PX;
    return Math.max(1, Math.min(MAX_VISIBLE_YEARS, Math.floor(available / yearColumnMinPx)));
};

/**
 * Most maintenance cells (with their month label) that fit in a single year
 * column before the ~11px labels start to collide. A year column is at least
 * YEAR_COLUMN_MIN_PX (160px) wide; a short month label needs ~30px including
 * the inter-cell gap, so ~5 labels fit. Beyond that we keep the status dots but
 * drop the per-cell labels (the month stays available on hover) — e.g. monthly
 * (12) and bi-monthly (6) cadences hide labels, quarterly (4) keeps them.
 */
export const MAX_LABELLED_CELLS_PER_YEAR = 5;

/** Whether per-cell month labels fit given the densest year's cell count. */
export const shouldShowCellLabels = (maxCellsPerYear: number): boolean => maxCellsPerYear <= MAX_LABELLED_CELLS_PER_YEAR;

/**
 * Width budget for one cell (dot + padding + gap) in a label-dropped dense row.
 * Used to widen year columns when a row packs many cells per year so the
 * shrunk-but-still-legible dots get breathing room instead of overflowing.
 */
export const DENSE_CELL_SLOT_PX = 22;

/**
 * Per-year minimum column width for the densest row on screen. Sparse rows keep
 * YEAR_COLUMN_MIN_PX; once a year exceeds the labelled threshold the column
 * widens to fit its cells. Because the grid columns are `1fr`, a larger minimum
 * reduces how many years are shown (computeVisibleYears) rather than letting the
 * dots overflow — i.e. dense cadences trade visible span for legible dots.
 */
export const yearColumnMinForDensity = (maxCellsPerYear: number): number =>
    maxCellsPerYear > MAX_LABELLED_CELLS_PER_YEAR ? Math.max(YEAR_COLUMN_MIN_PX, maxCellsPerYear * DENSE_CELL_SLOT_PX) : YEAR_COLUMN_MIN_PX;
